import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaFileAlt,
  FaCode,
  FaGraduationCap,
  FaCogs,
  FaBriefcase,
} from "react-icons/fa";
import openSound from "../assets/sounds/open-icon.wav";
import closeSound from "../assets/sounds/close-sound.mp3";
import desktopBg from "../assets/images/desktop-bg.png";
import Profile from "./profile";
import Resume from "./resume";
import Education from "./education";
import Skills from "./skills";
import Experience from "./experience";
import Projects from "./projects";
import DesktopIcon from "./desktop-icon";
import Window from "./window";

const MainScreen = ({ reset, isMobile }) => {
  const [openWindows, setOpenWindows] = useState({});
  const [windowStates, setWindowStates] = useState({});

  const openClickSound = new Audio(openSound);
  const closeClickSound = new Audio(closeSound);

  useEffect(() => {
    if (reset) {
      setOpenWindows({});
      setWindowStates({});
    }
  }, [reset]);

  const openWindow = (section) => {
    setOpenWindows({ ...openWindows, [section]: true });
    setWindowStates({
      ...windowStates,
      [section]: { minimized: false, maximized: true },
    });
    openClickSound.play();
  };

  const closeWindow = (section) => {
    const newOpenWindows = { ...openWindows };
    delete newOpenWindows[section];
    setOpenWindows(newOpenWindows);
    closeClickSound.play();
  };

  const toggleMinimize = (section) => {
    setWindowStates({
      ...windowStates,
      [section]: {
        ...windowStates[section],
        minimized: !windowStates[section].minimized,
      },
    });
  };

  const toggleMaximize = (section) => {
    setWindowStates({
      ...windowStates,
      [section]: {
        ...windowStates[section],
        maximized: !windowStates[section].maximized,
      },
    });
  };

  const renderWindowContent = (section) => {
    switch (section) {
      case "profile":
        return <Profile isMobile={isMobile} />;
      case "resume":
        return <Resume isMobile={isMobile} />;
      case "projects":
        return <Projects />;
      case "education":
        return <Education />;
      case "skills":
        return <Skills />;
      case "experience":
        return <Experience />;
      default:
        return <div>Select a section</div>;
    }
  };

  return (
    <div
      className="h-full w-full bg-blue-100 overflow-hidden relative rounded-md"
      style={{
        backgroundImage: `url(${desktopBg})`,
        backgroundSize: "cover",
        zIndex: 10,
        filter: isMobile ? "none" : "blur(0.5px)",
        opacity: isMobile ? 1 : 0.8,
      }}
    >
      <div
        className={`flex flex-wrap justify-center items-center ${
          isMobile ? "pt-12 px-4" : "p-12 pt-20"
        }`}
      >
        <div
          className={`grid ${
            isMobile
              ? "grid-cols-2 gap-8"
              : "grid-cols-2 sm:grid-cols-3 md:grid-cols-3 md:gap-2 gap-2"
          } w-full max-w-md mx-auto`}
        >
          <DesktopIcon
            Icon={FaUser}
            label="Profile"
            onClick={() => openWindow("profile")}
            isMobile={isMobile}
          />
          <DesktopIcon
            Icon={FaGraduationCap}
            label="Education"
            onClick={() => openWindow("education")}
            isMobile={isMobile}
          />
          <DesktopIcon
            Icon={FaCogs}
            label="Skills"
            onClick={() => openWindow("skills")}
            isMobile={isMobile}
          />

          <DesktopIcon
            Icon={FaBriefcase}
            label="Experience"
            onClick={() => openWindow("experience")}
            isMobile={isMobile}
          />
          <DesktopIcon
            Icon={FaCode}
            label="Projects"
            onClick={() => openWindow("projects")}
            isMobile={isMobile}
          />
          <DesktopIcon
            Icon={FaFileAlt}
            label="Resume"
            onClick={() => openWindow("resume")}
            isMobile={isMobile}
          />
        </div>
      </div>

      {Object.keys(openWindows).map((section) => (
        <Window
          key={section}
          title={section.charAt(0).toUpperCase() + section.slice(1)}
          onClose={() => closeWindow(section)}
          onMinimize={() => toggleMinimize(section)}
          onMaximize={() => toggleMaximize(section)}
          minimized={windowStates[section]?.minimized}
          maximized={windowStates[section]?.maximized}
          isMobile={isMobile}
        >
          {renderWindowContent(section)}
        </Window>
      ))}
    </div>
  );
};

export default MainScreen;
